import Divider from "@mui/material/Divider";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";

export interface CrudDialogHeaderProps {
  onClose?: () => void;
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  disabled?: boolean;
  position?: "fixed" | "absolute" | "sticky" | "static" | "relative";
  saveButton?: boolean;
  title?: string | null;
  customHeaderButton?: React.ReactNode;
}

export function CrudDialogHeader({
  onClose,
  primary,
  secondary,
  disabled,
  position,
  saveButton,
  title,
}: CrudDialogHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Одинаковые соседние крошки («Обращение > Обращение») схлопываются:
  // раздел, заголовок данных и шаг часто называются одним словом.
  const allBreadcrumbs = [secondary, title, primary]
    .filter(Boolean)
    .filter((text, index, list) => index === 0 || text !== list[index - 1]);

  // На узком экране три крошки с разделителями не помещаются — оставляем последнюю
  const breadcrumbs = (
    isMobile ? allBreadcrumbs.slice(-1) : allBreadcrumbs
  ).map((text, index) => (
    <Typography
      key={index}
      variant={isMobile ? "subtitle1" : "h6"}
      color={text === primary ? "textPrimary" : "textSecondary"}
      noWrap
      sx={{
        maxWidth: isMobile ? 300 : 500,
        fontWeight: isMobile ? 600 : 400,
      }}
    >
      {text}
    </Typography>
  ));

  return (
    <AppBar
      position={position}
      elevation={0}
      sx={{ bgcolor: "background.paper" }}
    >
      <Toolbar
        sx={{
          flexWrap: "wrap",
          ...(isMobile ? { py: 0.5, minHeight: "auto" } : {}),
        }}
      >
        <Tooltip title="Close dialog">
          <IconButton
            size={isMobile ? "medium" : "large"}
            edge="start"
            component="a"
            href="#close"
            onClick={(e) => {
              e.preventDefault();
              onClose?.();
            }}
            sx={{
              mr: isMobile ? 0.25 : 0.5,
              ml: isMobile ? -0.5 : 0,
            }}
          >
            <CloseIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Tooltip>

        <Breadcrumbs
          separator={<ChevronRightIcon />}
          aria-label="breadcrumb"
          sx={{ flex: isMobile ? "1 1 auto" : "0 1 auto", minWidth: 0 }}
        >
          {breadcrumbs}
        </Breadcrumbs>

        {saveButton && (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={disabled}
            size={isMobile ? "small" : "medium"}
            sx={{
              marginLeft: "auto",
              width: isMobile ? "auto" : 200,
              px: isMobile ? 1.5 : 2,
              flexShrink: 0,
            }}
          >
            {t("shared.form.submit", { defaultValue: "Сохранить" })}
          </Button>
        )}
      </Toolbar>
      <Divider />
    </AppBar>
  );
}
